import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion,AccordionSummary,FormControl,IconButton,InputLabel,MenuItem,Paper,Select,Stack,Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineEye,AiOutlineEyeInvisible } from "react-icons/ai";
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import getEndOfToday from '../../../helpers/getEndOfToday';
import { trackEventMatomo } from '../../../helpers/matomo';
import PlanoDeAcao from '../../../service/PlanoDeAcao';
import queryClient from '../../../service/query';
import { ChecklistSubitem } from '../../../types/checklist/types';
import type { planoDeAcaoType,planosType } from '../../../types/plano-de-acao/types';
import ImagesCmp from '../../ImagesCmp';
import OpenPlanoModal from './AbrePlano';

const ONEDAY = 86400000;

type props = {
  openModal: boolean,
  handleOpen: Function,
  selected: planosType | null,
};

type AccordeonProps = {
  subItem: ChecklistSubitem,
  planosList: planoDeAcaoType[],
  setAddedOptions: Function,
  indexSub: number
};

type addedState = {
  index: number,
  belongs: number | string
};

const PlanoDeAcaoService = new PlanoDeAcao();

function AccordeonNaoConf({ subItem, planosList, setAddedOptions, indexSub }: AccordeonProps) {
  const [expanded, setExpanded] = useState(true);
  const [showDetais, setShowDetails] = useState(true);

  const { t } = useTranslation('translation');

  return (
    <Accordion expanded={expanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon onClick={() => {
          trackEventMatomo('analise PA', 'click', 'acordeon', 'abre/fecha');
          setExpanded(!expanded);
        }} />}
      >
        <Typography variant='body2' style={{ wordBreak: 'break-all' }}>{subItem.nome}</Typography>
      </AccordionSummary>
      <Stack
        direction="column"
        spacing={1.5}
        justifyContent="flex-start"
        alignItems="flex-start"
        padding={'5px 20px 10px 20px'}
      >
        <Typography variant='body1' style={{ wordBreak: 'break-all', maxWidth: '95%' }}>Detalhes do subitem {subItem.nome}</Typography>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-start"
          alignItems="center"
        >

          <Typography variant='body2' fontWeight={600} color='textDisabled'>
            {showDetais
              ? `${t('ActionPlan.Modal.hideDetails')} `
              : `${t('ActionPlan.Modal.showDetails')} `}
          </Typography>
          <IconButton
            color='primary'
            onClick={() => {
              trackEventMatomo('analise PA', 'click', 'buttob', 'abre detalhes');
              setShowDetails((prev) => !prev);
            }}>
            {showDetais ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
          </IconButton>
        </Stack>
        {
          showDetais && (
            <Stack
              direction="column"
              spacing={1}
              paddingLeft={2}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <span>{t('ActionPlan.Modal.evaluation')}</span>
              {subItem?.avaliacao
                && subItem?.avaliacao?.map(ava => (
                  <Typography variant='body2' fontWeight={`${subItem.nota === ava.valor ? 600 : 'none'}`}>{ava.nome}</Typography>
                ))}
              {subItem?.ocorrencia
                && <span>{`${t('notifications.ticket.Comentario')}: ${subItem.ocorrencia}`}</span>}
              {subItem?.img
                && <>
                  <span>{t('ActionPlan.Modal.images')}</span>
                  <ImagesCmp images={subItem.img} />
                </>
              }
            </Stack>
          )
        }
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">{t('ActionPlan.Modal.selectActionPlanForSubitem')}</InputLabel>
          <Select
            defaultValue={""}
            onClick={() => trackEventMatomo('analise PA', 'click', 'select', 'seleciona valor')}
            onChange={(e) => {
              setAddedOptions((prev: addedState[]) => {
                const convertedValue = e.target.value === 'ignorar' ? e.target.value : Number(e.target.value);
                prev[indexSub].belongs = convertedValue;
                return [...prev];
              });
            }}
            label={t('ActionPlan.Modal.selectActionPlanForSubitem')}
          >
            {planosList && planosList.length && planosList.map((plano, index) => (
              <MenuItem value={index}>{plano.nome}</MenuItem>
            ))}
            <MenuItem value={"ignorar"}>{t('ActionPlan.Modal.ignore')}</MenuItem>

          </Select>
        </FormControl>
      </Stack>
    </Accordion >
  );
}

export default function Analise({
  openModal,
  handleOpen,
  selected,
}: props) {
  const { t } = useTranslation('translation');
  const initialStatePlanos: planoDeAcaoType[] = (selected?.naoConformidades as ChecklistSubitem[]).map((_sub, index) => ({
    naoConformidades: [],
    tasks: [],
    nome: `${t('ActionPlan.Modal.plan')} ${index + 1}`,
    empresa: selected?.empresa,
    idExecucao: selected?.idExecucao,
    responsavel: '',
    status: 'aberto',
    dataLimite: getEndOfToday().endDate.getTime() + (ONEDAY * 2)
  }));
  const [addedOptions, setAddedOptions] = useState<addedState[]>((selected?.naoConformidades as ChecklistSubitem[])?.map((_sub, i) => ({ index: i, belongs: "naoSelecionado" })));
  const [planosList, setPlanosList] = useState<planoDeAcaoType[]>(initialStatePlanos);
  const [planosForTreatment, setPlanosForTreatment] = useState<planoDeAcaoType[] | null>();
  const [loadingClosing, setLoadingClosing] = useState(false);
  const [open, setOpen] = useState(false);

  function handleOpenDetalhes() {
    setOpen(!open);
  }

  async function handleFinishAnalysis() {
    if (addedOptions.every(opt => opt.belongs === "ignorar")) {
      setLoadingClosing(true);
      const loadingToast = toast.loading(`${t('ActionPlan.Modal.rejectingAnalysis')}`);
      const token = await AttToken();
      if (token) {
        await PlanoDeAcaoService.startPlano(token, selected?.id as string, null);
        queryClient.resetQueries({ queryKey: ['GET_ANALISES_PENDING'] });
        queryClient.resetQueries({ queryKey: ['GET_ANALISES_FINALIZADAS'] });
      }
      handleOpen();
      handleOpenDetalhes();
      toast.update(loadingToast, { render: `${t('ActionPlan.Modal.analysisRejected')}`, type: "success", isLoading: false, autoClose: 2 });
      setLoadingClosing(false);
    } else {
      const newPlanos: planoDeAcaoType[] = [];
      addedOptions.forEach(opt => {
        if (typeof opt.belongs === 'number') {
          const naoConfsPlano = planosList[opt.belongs].naoConformidades;
          const currentNaoConf = selected?.naoConformidades[opt.index] as ChecklistSubitem;
          if (!naoConfsPlano || !naoConfsPlano.length) {
            planosList[opt.belongs].naoConformidades = [currentNaoConf];
            setPlanosList([...planosList]);
          } else {
            planosList[opt.belongs].naoConformidades = [...naoConfsPlano, currentNaoConf];
            setPlanosList([...planosList]);
          }
        }
      });
      planosList.forEach(plano => {
        if (plano.naoConformidades && plano.naoConformidades.length) {
          newPlanos.push(plano);
        }
      });
      setPlanosForTreatment(newPlanos);
      handleOpenDetalhes();
    }
  };

  function checkButtonInfo(action: 'style' | 'text'): string {
    if (!addedOptions.every(opt => opt.belongs !== "naoSelecionado")) {
      return action === 'style' ? 'button is-light' : `${t('ActionPlan.Modal.handlingNonConformities')}`;
    } else if (addedOptions.every(opt => opt.belongs === "ignorar")) {
      return action === 'style' ? 'button is-danger' : `${t('ActionPlan.Modal.ignorePlanCreation')}`;
    } else {
      return action === 'style' ? 'button is-success' : `${t('ActionPlan.Modal.detailPlan')}`;
    }
  }

  return (
    <>
      {open
        && planosForTreatment
        && planosForTreatment.length
        && (<OpenPlanoModal
          planos={planosForTreatment}
          setOpenModal={() => {
            trackEventMatomo('analise PA', 'click', 'modal', 'abre modal');
            setPlanosList(initialStatePlanos);
            setPlanosForTreatment(null);
            handleOpenDetalhes();
          }}
          openModal={open}
          setPlanos={setPlanosForTreatment}
          handleOpen={handleOpenDetalhes}
          analiseId={selected?.id as string}
          handleCloseFather={handleOpen}
        />
        )}
      <Dialog
        fullWidth
        maxWidth='md'
        open={openModal}
        onClose={() => handleOpen()}
        scroll='paper'
      >
        <DialogTitle>{`${t('ActionPlan.Modal.analyzing')} ${selected?.nomeForm}`}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleOpen()}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          <Typography
            sx={{ color: '#47AA9C' }}
            variant='subtitle1'
            fontWeight={600}
            textAlign={'center'}>
            {t('ActionPlan.Modal.nonConformities')} {'(Subitens)'}:
          </Typography>
          <Stack direction='column' spacing={1} width='100%' justifyContent='flex-start' alignItems='stretch'>
            {(selected?.naoConformidades as ChecklistSubitem[])?.map((subItem, index) =>
              <AccordeonNaoConf
                key={index}
                subItem={subItem}
                planosList={planosList}
                setAddedOptions={setAddedOptions}
                indexSub={index}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <>
            {!addedOptions.every(opt => opt.belongs !== "naoSelecionado") ? (
              <Stack width='100%' alignItems='center' justifyContent='center'>
                <Paper sx={{ backgroundColor: '#f2c809', padding: 1 }}>
                  {t('ActionPlan.Modal.addNonConformitiesOrIgnore')}
                </Paper>
              </Stack>
            ) :
              <Button
                className={checkButtonInfo('style')}
                disabled={!addedOptions.every(opt => opt.belongs !== "naoSelecionado") || loadingClosing}
                onClick={() => {
                  trackEventMatomo('analise PA', 'click', 'button', 'abre PA');
                  handleFinishAnalysis();
                }}
              >
                {checkButtonInfo('text')}
              </Button>
            }
          </>
        </DialogActions>
      </Dialog>
    </>
  );
}
